import {
  ReportDataLevel,
  DepotConfiguration,
  DeviceInfo,
  Server,
  Connection,
  ServerConfig,
} from '@sas-dvr/ltjs-transport';
import { getUriParameter } from '@sas-dvr/ltjs-commons/ltjsUtil';
import { getSASApplicationNameHeader } from '@sas/nova-core/axios/interceptors/sas-app-name-header';
import { VASDK_APP_NAME } from '../init/initNovaCore';
import { ltjsGlobalNamespace } from '@sas-dvr/ltjs-commons/ltjsInit';

const guestTokenMap = new Map<string, null | string | Promise<string>>();
export function getGuestAuthorization(server: string, force: boolean = false) {
  const token = guestTokenMap.get(server);
  if (token instanceof Promise) {
    return token;
  }

  if (!force) {
    if (token) {
      return Promise.resolve(token);
    }
    if (token === null) {
      return Promise.reject('No guest token available');
    }
  }

  const promise = fetch(`${server}/SASLogon/oauth/token`, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      Authorization: 'Basic c2FzLm1vYmlsZWJpOg==',
      Accept: 'application/json, */*',
    },
    body: new URLSearchParams('grant_type=guest'),
  })
    .then((resp) => resp.json())
    .then((respJson) => {
      if (!respJson.access_token) {
        throw new Error(
          `Unable to retrieve access token. ${respJson.error}: ${respJson.error_description}`
        );
      }
      const authToken = `bearer ${respJson.access_token}`;
      guestTokenMap.set(server, authToken);
      return authToken;
    })
    .catch((e) => {
      guestTokenMap.set(server, null);
      return Promise.reject(e);
    });

  guestTokenMap.set(server, promise);
  return promise;
}

export async function createFetchHeaders(serverUrl: string, forceFetchToken: boolean = false) {
  const headers = createBasicFetchHeaders();
  const authorization = await getGuestAuthorization(serverUrl, forceFetchToken);
  headers.set('Authorization', authorization);
  return headers;
}

function createBasicFetchHeaders(): Headers {
  return new Headers({
    'VAT-AppName': 'HTML5 8.3',
    'VAT-DeviceId': 'TEST',
    Authorization: 'Basic c2FzLm1vYmlsZWJpOg==',
    Accept: 'application/json, */*',
  });
}

export interface DepotConfigurationOptions {
  dataLevel: ReportDataLevel;
  highContrast?: boolean;
}

let _depotCount = 0;
function _createDepotPath() {
  _depotCount++;
  return `/webDepot/caches/${_depotCount}`;
}

function getDataLevel(dataLevel: ReportDataLevel) {
  const level = getUriParameter('dataLevel');
  if (!level) {
    return dataLevel;
  }
  switch (level.toUpperCase()) {
    case 'BASELINE':
      return ReportDataLevel.BASELINE;
    case 'INTERACTIVE':
      return ReportDataLevel.INTERACTIVE;
    case 'NODATA':
      return ReportDataLevel.NODATA;
  }
  return dataLevel;
}

export function createDepotConfiguration(opts: DepotConfigurationOptions) {
  const conf = new DepotConfiguration();
  conf.setCacheDirectoryPath(_createDepotPath());
  conf.setShouldAutoConnect(true);
  conf.setShouldAutoUpdateSubscribedReports(false);
  conf.setShouldAutoStartReportAssetDownloads(true);
  conf.setSupportsTransportServerLogging(false);
  conf.setGenerateReportDataLevel(getDataLevel(opts.dataLevel));
  conf.setSupportsTethered(false);
  conf.setShouldPerformPeriodicDeviceChecks(false);
  conf.setMaximumNumberOfConcurrentReportDownloads(1);
  conf.setShouldAutoDownloadPrintedReports(false);
  conf.setGenerateReportTimeout(55); //S1139556
  conf.setAutoUpdateFrequency(30); // previous default
  conf.setGenerateReportImagesTimeout(0.5);
  conf.setAllowFutureServers(true);
  conf.setMaximumHistoryEntries(18); //S1390596
  conf.setClientid('sas.mobilebi');
  conf.setAllowMetadataFailure(true);
  const deviceInfo = new DeviceInfo();
  deviceInfo.setDeviceModel(navigator.platform);
  deviceInfo.setDeviceType('web');
  deviceInfo.setOsVersion(navigator.appVersion);
  deviceInfo.setMobileAppVersion('HTML5 8.3');
  deviceInfo.setDeviceId('va-sdk');
  conf.setDeviceInfo(deviceInfo);
  conf.setCheckForSearchSupport(false);
  conf.setShouldUseCatalogSearchApi(false);
  conf.setCheckForReportImagesSupport(false);
  conf.setShouldGetCadenceVersion(false);
  conf.setUseHighContrastReportStyle(opts.highContrast ?? false);
  conf.setIsPrivateDataSupported(true);
  conf.setAuditid(getSASApplicationNameHeader() || VASDK_APP_NAME);
  conf.setShouldAllowAuditActivityService(true);

  // Reduce network traffic in Jest tests
  if (ltjsGlobalNamespace.jest) {
    conf.setShouldPerformPeriodicReportUpdateChecks(false);
    conf.setShouldGetFeatureFlagsOnAuth(false);
    conf.setShouldAllowAuditActivityService(false);
  }

  return conf;
}

/**
 * @internal
 * @param hostUrl
 * @param serverVersionId for creating fake servers.
 */
export function createServer(hostUrl: string, serverVersionId: number): Server;
export function createServer(hostUrl: string): Server;
export function createServer(hostUrl: string, serverVersionId?: number) {
  const server = new Server();
  const parsedURL = document.createElement('a');
  parsedURL.href = hostUrl;
  const isSecure = parsedURL.protocol === 'https:';
  const port = parseInt(parsedURL.port);

  server.setScheme(isSecure ? 'https' : 'http');
  server.setHostName(parsedURL.hostname);
  server.setUseSSL(isSecure);
  server.setPort(port || (isSecure ? 443 : 80));

  // Set up fake information for "local server"
  if (serverVersionId !== undefined) {
    server.setVersionId(serverVersionId);
    server.setMeta(new ServerConfig());
  }
  return server;
}

export function createConnection(server: Server): Connection {
  const connection = new Connection();
  connection.setShouldAutoConnect(false);
  connection.setServer(server);
  return connection;
}
